import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { PageHeader, SearchBar, DataTable, Badge, Pagination, Loading, formatCurrency, formatDate, usePersistedState, BackButton } from '../components/Shared';

const STATUTS = ['', 'Brouillon', 'Envoyé', 'Validé', 'Refusé', 'Expiré'];

function statutColor(s) {
  if (s === 'Validé') return 'var(--accent-emerald)';
  if (s === 'Envoyé') return 'var(--accent-blue)';
  if (s === 'Refusé' || s === 'Expiré') return '#ef4444';
  return 'var(--text-secondary)';
}

export default function Devis() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [search, setSearch] = usePersistedState('devis_search', '');
  const [page, setPage] = usePersistedState('devis_page', 1);
  const [statut, setStatut] = usePersistedState('devis_statut', '');
  const [client, setClient] = usePersistedState('devis_client', '');
  const [archives, setArchives] = usePersistedState('devis_archives', false);

  const { data, loading } = useApi('/devis', { search, page, limit: 25, statut, client, archives: archives ? 'true' : undefined }, [search, page, statut, client, archives]);

  // Stats rapides
  const stats = data?.stats || {};

  const columns = [
    { header: 'N° Devis', render: r => <strong style={{ color: 'var(--accent-blue)' }}>{r.devis_id}</strong>, width: '150px' },
    { header: 'Client', render: r => <span>{r.client_name}</span> },
    { header: 'Formation', render: r => <span style={{ fontSize: '0.85rem' }}>{r.intitule_formation?.substring(0, 45)}{r.intitule_formation?.length > 45 ? '…' : ''}</span>, mobileHide: true },
    { header: 'Total HT', render: r => <strong>{formatCurrency(r.total_ht)}</strong>, width: '110px' },
    { header: 'Demande', render: r => formatDate(r.date_demande), width: '105px', mobileHide: true },
    { header: 'Validité', render: r => {
      if (!r.date_validite) return '—';
      const expired = new Date(r.date_validite) < new Date() && r.statut_devis !== 'Validé';
      return <span style={{ color: expired ? '#ef4444' : 'inherit' }}>{formatDate(r.date_validite)}</span>;
    }, width: '105px' },
    { header: 'Statut', render: r => <Badge style={{ color: statutColor(r.statut_devis) }}>{r.statut_devis}</Badge>, width: '100px' },
    { header: 'Email', render: r => r.email_client
        ? <span style={{ color: 'var(--accent-emerald)', fontSize: '0.8rem' }}>✓ {r.email_client}</span>
        : <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>—</span>, mobileHide: true },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Devis"
        subtitle={data ? `${data.total} devis` : ''}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <BackButton />
            {hasRole('utilisateur') && <button className="btn-primary" onClick={() => navigate('/devis/nouveau')}>+ Nouveau devis</button>}
          </div>
        }
      />

      {/* Compteurs statuts */}
      {data?.statuts && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {data.statuts.map(s => (
            <div key={s.statut_devis} onClick={() => setStatut(statut === s.statut_devis ? '' : s.statut_devis)}
              style={{ padding: '0.5rem 1rem', borderRadius: '8px', background: 'var(--bg-card)', border: `1px solid ${statut === s.statut_devis ? 'var(--accent-blue)' : 'var(--border)'}`, cursor: 'pointer', fontSize: '0.85rem' }}>
              <span style={{ fontWeight: 700, color: statutColor(s.statut_devis) }}>{s.nb}</span>
              <span style={{ color: 'var(--text-secondary)', marginLeft: '0.4rem' }}>{s.statut_devis}</span>
              {s.total_ht > 0 && <span style={{ color: 'var(--text-tertiary)', marginLeft: '0.4rem', fontSize: '0.78rem' }}>({formatCurrency(s.total_ht)})</span>}
            </div>
          ))}
        </div>
      )}

      <div className="toolbar">
        <SearchBar value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="N° devis, client, formation..." />
        <div className="toolbar-filters">
          {STATUTS.map(s => (
            <button key={s} className={`filter-chip ${statut === s ? 'active' : ''}`}
              onClick={() => { setStatut(s); setPage(1); }}>
              {s || 'Tous'}
            </button>
          ))}
          <button className={`filter-chip ${archives ? 'active' : ''}`}
            onClick={() => { setArchives(a => !a); setPage(1); }}
            style={archives ? { borderColor: '#f87171', background: 'rgba(239,68,68,0.15)', color: '#f87171' } : {}}>
            🗄 Archivés
          </button>
        </div>
      </div>

      {loading ? <Loading /> : (
        <>
          <DataTable columns={columns} data={data?.data} onRowClick={row => navigate(`/devis/${row.devis_id}`)} />
          <Pagination page={page} total={data?.total || 0} limit={25} onChange={setPage} />
        </>
      )}
    </div>
  );
}
